import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Upload,
  Loader2,
  CheckCircle2,
  FileText,
  RefreshCw,
  Link2,
  ArrowRight,
  Calendar,
  Building2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';
import { formatCurrency, cn } from '@/lib/utils';

type TabType = 'workspace' | 'history';

export function ReconciliationPage() {
  const { t, i18n } = useTranslation('reconciliation');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  // State from navigation (e.g., after import)
  const importedCount = location.state?.imported;

  const [activeTab, setActiveTab] = useSessionState<TabType>(
    'travel:reconciliation:activeTab',
    'workspace'
  );
  const [selectedAccountId, setSelectedAccountId] = useSessionState<string>(
    'travel:reconciliation:selectedAccountId',
    ''
  );
  const [selectedReconciliationId, setSelectedReconciliationId] = useState<string | null>(null);
  const [newRecModalOpen, setNewRecModalOpen] = useState(false);
  const [startDate, setStartDate] = useSessionState<string>(
    'travel:reconciliation:startDate',
    (() => {
      const date = new Date();
      date.setDate(1);
      return date.toISOString().split('T')[0] ?? '';
    })()
  );
  const [endDate, setEndDate] = useSessionState<string>(
    'travel:reconciliation:endDate',
    new Date().toISOString().split('T')[0] ?? ''
  );
  const [statementBalance, setStatementBalance] = useState('');

  // Selected items for matching
  const [selectedBankTx, setSelectedBankTx] = useState<string | null>(null);
  const [selectedJournalLine, setSelectedJournalLine] = useState<string | null>(null);

  // Filter state
  const [bankFilter, setBankFilter] = useSessionState<'all' | 'unmatched' | 'matched'>(
    'travel:reconciliation:bankFilter',
    'unmatched'
  );
  const [journalFilter, setJournalFilter] = useSessionState<'all' | 'unmatched'>(
    'travel:reconciliation:journalFilter',
    'unmatched'
  );

  // Queries
  const { data: bankAccounts } = trpc.account.list.useQuery({
    type: 'ASSET',
    subType: 'BANK',
    isActive: true,
  });

  const { data: reconciliations, isLoading: recLoading } = trpc.reconciliation.list.useQuery({
    accountId: selectedAccountId || undefined,
  });

  const { data: bankTransactions, isLoading: txLoading } =
    trpc.reconciliation.getBankTransactions.useQuery(
      {
        accountId: selectedAccountId,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        isReconciled:
          bankFilter === 'matched' ? true : bankFilter === 'unmatched' ? false : undefined,
      },
      { enabled: !!selectedAccountId }
    );

  const { data: journalEntries, isLoading: jeLoading } =
    trpc.reconciliation.getUnmatchedJournalEntries.useQuery(
      {
        accountId: selectedAccountId,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      },
      { enabled: !!selectedAccountId && journalFilter === 'unmatched' }
    );

  const { data: currentReconciliation } = trpc.reconciliation.byId.useQuery(
    selectedReconciliationId!,
    { enabled: !!selectedReconciliationId }
  );

  const utils = trpc.useUtils();

  // Mutations
  const createRecMutation = trpc.reconciliation.create.useMutation({
    onSuccess: (data) => {
      utils.reconciliation.list.invalidate();
      setSelectedReconciliationId(data.id);
      setNewRecModalOpen(false);
    },
  });

  const matchMutation = trpc.reconciliation.matchTransaction.useMutation({
    onSuccess: () => {
      utils.reconciliation.getBankTransactions.invalidate();
      utils.reconciliation.getUnmatchedJournalEntries.invalidate();
      utils.reconciliation.byId.invalidate();
      setSelectedBankTx(null);
      setSelectedJournalLine(null);
    },
  });

  const autoMatchMutation = trpc.reconciliation.autoMatch.useMutation({
    onSuccess: () => {
      utils.reconciliation.getBankTransactions.invalidate();
      utils.reconciliation.getUnmatchedJournalEntries.invalidate();
      utils.reconciliation.byId.invalidate();
    },
  });

  const completeMutation = trpc.reconciliation.complete.useMutation({
    onSuccess: () => {
      utils.reconciliation.list.invalidate();
      utils.reconciliation.byId.invalidate();
    },
  });

  // Handlers
  const handleCreateReconciliation = () => {
    if (!selectedAccountId || !statementBalance) return;

    createRecMutation.mutate({
      accountId: selectedAccountId,
      periodStart: new Date(startDate),
      periodEnd: new Date(endDate),
      statementBalance: parseFloat(statementBalance),
    });
  };

  const handleMatch = () => {
    if (!selectedBankTx || !selectedReconciliationId) return;

    matchMutation.mutate({
      reconciliationId: selectedReconciliationId,
      bankTransactionId: selectedBankTx,
      journalEntryLineId: selectedJournalLine || undefined,
      matchType: 'MANUAL',
    });
  };

  const handleAutoMatch = () => {
    if (!selectedReconciliationId) return;
    autoMatchMutation.mutate({ reconciliationId: selectedReconciliationId });
  };

  const handleComplete = () => {
    if (!selectedReconciliationId) return;
    completeMutation.mutate({ id: selectedReconciliationId });
  };

  const statusColors: Record<string, string> = {
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    RECONCILED: 'bg-green-100 text-green-700',
    DISCREPANCY: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/travel/accounting/reconciliation/import">
            <Button variant="outline" className="h-8">
              <Upload className="mr-2 h-4 w-4" />
              {t('import.title')}
            </Button>
          </Link>
          <Button className="h-8" onClick={() => setNewRecModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('workspace.newReconciliation')}
          </Button>
        </div>
      </div>

      {/* Import success message */}
      {importedCount && (
        <div className="flex items-center gap-3 border border-green-200 bg-green-50 p-4 text-green-800">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
          <p>{t('import.importSuccess', { count: importedCount })}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {[
            { id: 'workspace', label: t('workspace.title'), icon: Link2 },
            { id: 'history', label: t('list.title'), icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Account Selection */}
      <div className="border-border bg-card border p-4">
        <div className="flex items-center gap-4">
          <Building2 className="text-muted-foreground h-5 w-5" />
          <select
            value={selectedAccountId}
            onChange={(e) => {
              setSelectedAccountId(e.target.value);
              setSelectedReconciliationId(null);
            }}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 max-w-md flex-1 border px-4 py-2 focus:outline-none focus:ring-1"
          >
            <option value="">{t('import.selectAccountPlaceholder')}</option>
            {bankAccounts?.accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} - {account.name} ({formatCurrency(account.balance, account.currency)}
                )
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            />
            <ArrowRight className="text-muted-foreground h-4 w-4" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            />
          </div>
        </div>
      </div>

      {/* Workspace Tab */}
      {activeTab === 'workspace' && selectedAccountId && (
        <div className="space-y-6">
          {/* Current Reconciliation Info */}
          {currentReconciliation && (
            <div className="border-border bg-card border p-4">
              <div className="flex items-center justify-between">
                <div className="grid grid-cols-5 gap-6">
                  <div>
                    <p className="text-muted-foreground text-xs">{t('workspace.openingBalance')}</p>
                    <p className="font-mono font-medium">
                      {formatCurrency(
                        currentReconciliation.openingBalance,
                        currentReconciliation.account.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t('workspace.closingBalance')}</p>
                    <p className="font-mono font-medium">
                      {formatCurrency(
                        currentReconciliation.closingBalance,
                        currentReconciliation.account.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">
                      {t('workspace.statementBalanceLabel')}
                    </p>
                    <p className="font-mono font-medium">
                      {formatCurrency(
                        currentReconciliation.statementBalance,
                        currentReconciliation.account.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t('workspace.difference')}</p>
                    <p
                      className={cn(
                        'font-mono font-medium',
                        Math.abs(currentReconciliation.difference) > 0.01
                          ? 'text-red-600'
                          : 'text-green-600'
                      )}
                    >
                      {formatCurrency(
                        currentReconciliation.difference,
                        currentReconciliation.account.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t('list.status')}</p>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 text-xs font-medium',
                        statusColors[currentReconciliation.status]
                      )}
                    >
                      {t(`workspace.status.${currentReconciliation.status}`)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={handleAutoMatch}
                    disabled={
                      autoMatchMutation.isPending || currentReconciliation.status !== 'IN_PROGRESS'
                    }
                  >
                    {autoMatchMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    {t('workspace.autoMatch')}
                  </Button>
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={handleComplete}
                    disabled={
                      completeMutation.isPending ||
                      currentReconciliation.status !== 'IN_PROGRESS' ||
                      Math.abs(currentReconciliation.difference) > 0.01
                    }
                  >
                    {completeMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                    )}
                    {t('workspace.complete')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Match action bar */}
          {selectedBankTx &&
            selectedReconciliationId &&
            currentReconciliation?.status === 'IN_PROGRESS' && (
              <div className="bg-primary/10 flex items-center justify-center gap-4 p-3">
                <span className="text-sm font-medium">{t('workspace.selectToMatch')}</span>
                <Button
                  size="sm"
                  className="h-8"
                  onClick={handleMatch}
                  disabled={matchMutation.isPending}
                >
                  {matchMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 h-4 w-4" />
                  )}
                  {t('workspace.matchSelected')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    setSelectedBankTx(null);
                    setSelectedJournalLine(null);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  {tCommon('cancel')}
                </Button>
              </div>
            )}

          {/* Split View */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Bank Transactions Panel */}
            <div className="border-border bg-card border">
              <div className="flex items-center justify-between border-b p-4">
                <h3 className="font-semibold">{t('workspace.bankTransactions')}</h3>
                <div className="flex items-center gap-2">
                  {['all', 'unmatched', 'matched'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setBankFilter(filter as typeof bankFilter)}
                      className={cn(
                        'px-3 py-1 text-xs font-medium transition-colors',
                        bankFilter === filter
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      )}
                    >
                      {t(`workspace.${filter}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="max-h-[500px] overflow-auto">
                {txLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="text-primary h-8 w-8 animate-spin" />
                  </div>
                ) : !bankTransactions?.transactions.length ? (
                  <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
                    <FileText className="mb-4 h-12 w-12 opacity-50" />
                    <p>{t('workspace.noTransactions')}</p>
                    <Link to="/travel/accounting/reconciliation/import">
                      <Button variant="link" size="sm">
                        {t('workspace.importTransactions')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y">
                    {bankTransactions.transactions.map((tx) => (
                      <div
                        key={tx.id}
                        onClick={() => {
                          if (
                            tx.isReconciled ||
                            !selectedReconciliationId ||
                            currentReconciliation?.status !== 'IN_PROGRESS'
                          )
                            return;
                          setSelectedBankTx(selectedBankTx === tx.id ? null : tx.id);
                        }}
                        className={cn(
                          'p-3 transition-colors',
                          tx.isReconciled
                            ? 'bg-green-50/50'
                            : selectedBankTx === tx.id
                              ? 'bg-primary/10 cursor-pointer'
                              : selectedReconciliationId &&
                                  currentReconciliation?.status === 'IN_PROGRESS'
                                ? 'hover:bg-muted/50 cursor-pointer'
                                : ''
                        )}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-medium">
                                {tx.description || tx.counterpartyName || 'Transaction'}
                              </p>
                              {tx.isReconciled && (
                                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
                              )}
                            </div>
                            <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                              <span>
                                {new Date(tx.transactionDate).toLocaleDateString(dateLocale)}
                              </span>
                              {tx.reference && (
                                <>
                                  <span>•</span>
                                  <span>{tx.reference}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={cn(
                                'font-mono font-medium',
                                tx.transactionType === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                              )}
                            >
                              {tx.transactionType === 'CREDIT' ? '+' : '-'}
                              {formatCurrency(tx.amount, tx.currency)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Journal Entries Panel */}
            <div className="border-border bg-card border">
              <div className="flex items-center justify-between border-b p-4">
                <h3 className="font-semibold">{t('workspace.journalEntries')}</h3>
                <div className="flex items-center gap-2">
                  {['unmatched'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setJournalFilter(filter as typeof journalFilter)}
                      className={cn(
                        'px-2 py-0.5 text-xs font-medium transition-colors',
                        journalFilter === filter
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      )}
                    >
                      {t(`workspace.${filter}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="max-h-[500px] overflow-auto">
                {jeLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="text-primary h-8 w-8 animate-spin" />
                  </div>
                ) : !journalEntries?.length ? (
                  <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
                    <FileText className="mb-4 h-12 w-12 opacity-50" />
                    <p>{t('workspace.noJournalEntries')}</p>
                    <Link to="/travel/accounting/journal-entries/new">
                      <Button variant="link" size="sm">
                        {t('workspace.createJournalEntry')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y">
                    {journalEntries.map((line) => (
                      <div
                        key={line.id}
                        onClick={() => {
                          if (
                            !selectedBankTx ||
                            !selectedReconciliationId ||
                            currentReconciliation?.status !== 'IN_PROGRESS'
                          )
                            return;
                          setSelectedJournalLine(selectedJournalLine === line.id ? null : line.id);
                        }}
                        className={cn(
                          'p-3 transition-colors',
                          selectedJournalLine === line.id
                            ? 'bg-primary/10 cursor-pointer'
                            : selectedBankTx &&
                                selectedReconciliationId &&
                                currentReconciliation?.status === 'IN_PROGRESS'
                              ? 'hover:bg-muted/50 cursor-pointer'
                              : ''
                        )}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {line.journalEntry.description || line.description || 'Journal Entry'}
                            </p>
                            <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                              <span>{line.journalEntry.entryNumber}</span>
                              <span>•</span>
                              <span>
                                {new Date(line.journalEntry.date).toLocaleDateString(dateLocale)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={cn(
                                'font-mono font-medium',
                                line.amount >= 0 ? 'text-green-600' : 'text-red-600'
                              )}
                            >
                              {line.amount >= 0 ? '+' : ''}
                              {formatCurrency(Math.abs(line.amount), 'TRY')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="border-border bg-card overflow-hidden border">
          {recLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="text-primary h-8 w-8 animate-spin" />
            </div>
          ) : !reconciliations?.reconciliations.length ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
              <FileText className="mb-4 h-12 w-12 opacity-50" />
              <p>{t('list.noReconciliations')}</p>
              <p className="text-sm">{t('list.getStarted')}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('list.account')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('list.period')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('list.status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('workspace.statementBalanceLabel')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('list.difference')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">{t('list.items')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('list.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reconciliations.reconciliations.map((rec) => (
                  <tr key={rec.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <p className="font-medium">{rec.account.name}</p>
                      <p className="text-muted-foreground text-xs">{rec.account.code}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(rec.periodStart).toLocaleDateString(dateLocale)} -{' '}
                      {new Date(rec.periodEnd).toLocaleDateString(dateLocale)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 text-xs font-medium',
                          statusColors[rec.status]
                        )}
                      >
                        {t(`workspace.status.${rec.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {formatCurrency(rec.statementBalance, rec.account.currency)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <span
                        className={cn(
                          Math.abs(rec.difference) > 0.01 ? 'text-red-600' : 'text-green-600'
                        )}
                      >
                        {formatCurrency(rec.difference, rec.account.currency)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">{rec._count.items}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedAccountId(rec.accountId);
                          setSelectedReconciliationId(rec.id);
                          setActiveTab('workspace');
                        }}
                      >
                        {rec.status === 'IN_PROGRESS' ? t('list.continue') : t('list.view')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* New Reconciliation Modal */}
      {newRecModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">{t('workspace.newReconciliation')}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('import.selectAccount')}</label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="border-border bg-background mt-1 h-8 w-full border px-4 py-2"
                >
                  <option value="">{t('import.selectAccountPlaceholder')}</option>
                  {bankAccounts?.accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('workspace.startDate')}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border-border bg-background mt-1 h-8 w-full border px-4 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('workspace.endDate')}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border-border bg-background mt-1 h-8 w-full border px-4 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t('workspace.statementBalance')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={statementBalance}
                  onChange={(e) => setStatementBalance(e.target.value)}
                  placeholder="0.00"
                  className="border-border bg-background mt-1 h-8 w-full border px-4 py-2"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setNewRecModalOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                onClick={handleCreateReconciliation}
                disabled={createRecMutation.isPending || !selectedAccountId || !statementBalance}
              >
                {createRecMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {tCommon('create')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
